// ══════════════════════════════════════════════════════════════
// COMPASS – User Signature Effects (US-013)
// ══════════════════════════════════════════════════════════════

import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { UserSignatureActions } from './user-signature.actions';
import { ToastActions } from '../toast/toast.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { getMySignature } from '../../api/fn/user-signature/get-my-signature';
import { uploadMySignature } from '../../api/fn/user-signature/upload-my-signature';
import { failWith } from '../../utils/api-error.utils';

/** No failure toast: the signature card shows a failed load (with a retry) and a refused upload itself. */
@Injectable()
export class UserSignatureEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);

  /** A 404 is no signature stored yet, and a 403 no signature to manage (not a Compliance Officer): neither is a
   *  failure. The backend checks the permission in any company, which the frontend cannot see from one company. */
  readonly loadSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSignatureActions.loadSignature),
      switchMap(() =>
        getMySignature(this.http, this.apiConfig.rootUrl).pipe(
          map((response) => UserSignatureActions.loadSignatureSuccess({ signature: response.body ?? null })),
          catchError((error: unknown) => {
            const status = error instanceof HttpErrorResponse ? error.status : undefined;
            if (status === 404) return of(UserSignatureActions.loadSignatureSuccess({ signature: null }));
            if (status === 403) return of(UserSignatureActions.loadSignatureForbidden());
            return failWith('COMPANY.PROFILE.SIGNATURE.ERRORS.LOAD',
              (message) => UserSignatureActions.loadSignatureFailure({ error: message }))(error);
          }),
        ),
      ),
    ),
  );

  /** A write: a second upload is ignored while the first runs. */
  readonly uploadSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSignatureActions.uploadSignature),
      exhaustMap(({ file }) =>
        uploadMySignature(this.http, this.apiConfig.rootUrl, { body: { file } }).pipe(
          switchMap((response) => [
            UserSignatureActions.uploadSignatureSuccess({ signature: response.body }),
            ToastActions.showToast({ message: 'COMPANY.PROFILE.SIGNATURE.UPLOAD_SUCCESS', notificationType: 'success' }),
          ]),
          catchError(failWith('COMPANY.PROFILE.SIGNATURE.ERRORS.UPLOAD',
            (message) => UserSignatureActions.uploadSignatureFailure({ error: message }))),
        ),
      ),
    ),
  );
}
